import { create } from 'zustand';

export type SessionVisualState = 'idle' | 'working' | 'completed' | 'exited';

export interface TerminalSessionInfo {
  id: number;
  title: string;
  status: 'active' | 'closed';
  projectPath: string;
  isAgent: boolean;
  visualState: SessionVisualState;
}

interface PendingRunCommand {
  command: string;
  title: string;
  projectPath: string;
  /** Optional shell override for this run command (e.g. 'bash', 'wsl'). */
  shell?: string;
  /** Optional environment overrides for the spawned shell (e.g.
   *  `{ CLAUDE_CONFIG_DIR }` to scope a Claude identity auth flow). */
  env?: Record<string, string>;
  /** What this session IS (stamped as ATELIER_SESSION_KIND so the daemon
   *  records it and an adopted/orphaned session can be reconstituted):
   *  'run' = RunButton config, 'auth' = identity sign-in flow. */
  kind?: 'run' | 'auth';
  /** Run-config name (kind='run'); stamped as ATELIER_RUN_CONFIG so the Run
   *  button can re-bind to an adopted still-running session. */
  runConfig?: string;
}

/** Serializable snapshot of terminal state for persistence across app restarts. */
interface PersistedTerminalState {
  sessions: [number, TerminalSessionInfo][];
  activeTerminalIds: [string, number | null][];
  runSessionIds: [string, number][];
}

const PERSIST_KEY = 'ateliercode:persisted-terminals';

interface TerminalState {
  /** All terminal sessions across all projects, keyed by PTY session ID */
  sessions: Map<number, TerminalSessionInfo>;
  /** Per-project active terminal ID */
  activeTerminalIds: Map<string, number | null>;
  /** Incremented to request focus on the active terminal */
  focusTrigger: number;
  /** Pending run command from RunButton (consumed by TerminalTab) */
  pendingCommand: PendingRunCommand | null;
  /** Incremented to signal the workspace to switch to the terminal tab */
  tabSwitchRequest: number;
  /** Active run session per project (projectPath → terminal session ID) */
  runSessionIds: Map<string, number>;
  /** Which project's terminals are currently attached (xterm.js mounted) */
  attachedProject: string | null;
  /** Projects that have been initialized (prevents phantom terminals on remount) */
  initializedProjects: Set<string>;

  addSession: (id: number, title: string, projectPath: string, isAgent?: boolean) => void;
  removeSession: (id: number) => void;
  /** Atomically replace a placeholder session ID with the real one (no flash). */
  replaceSessionId: (oldId: number, newId: number) => void;
  setActiveTerminal: (projectPath: string, id: number | null) => void;
  renameSession: (id: number, title: string) => void;
  updateSessionStatus: (id: number, status: 'active' | 'closed') => void;
  getProjectSessions: (projectPath: string) => TerminalSessionInfo[];
  getActiveTerminalId: (projectPath: string) => number | null;
  requestFocus: () => void;
  setPendingCommand: (cmd: PendingRunCommand | null) => void;
  /** Request the workspace switch to the terminal tab */
  requestTabSwitch: () => void;
  setRunSession: (projectPath: string, sessionId: number | null) => void;
  setAttachedProject: (projectPath: string | null) => void;
  updateVisualState: (id: number, state: SessionVisualState) => void;
  markProjectInitialized: (projectPath: string) => void;
  /** Undo markProjectInitialized so the next TerminalTab mount re-syncs with
   *  the daemon — used when a sync attempt failed (e.g. IPC mid-reconnect). */
  unmarkProjectInitialized: (projectPath: string) => void;
  isProjectInitialized: (projectPath: string) => boolean;
  /** Save current session state to localStorage (before app update). */
  saveSessionsToStorage: () => void;
  /** Load previously persisted sessions from localStorage. Returns null if none. */
  loadSessionsFromStorage: () => PersistedTerminalState | null;
  /** Clear persisted sessions from localStorage. */
  clearPersistedStorage: () => void;
  /** Restore sessions from localStorage into the store (called on app startup). */
  restoreFromPersistence: () => boolean;
  /** Clear initialized projects to allow fresh sync with daemon. */
  clearInitializedProjects: () => void;
}

export const useTerminalStore = create<TerminalState>((set, get) => ({
  sessions: new Map(),
  activeTerminalIds: new Map(),
  focusTrigger: 0,
  pendingCommand: null,
  tabSwitchRequest: 0,
  runSessionIds: new Map(),
  attachedProject: null,
  initializedProjects: new Set(),

  addSession: (id, title, projectPath, isAgent = false) =>
    set((state) => {
      const sessions = new Map(state.sessions);
      sessions.set(id, { id, title, status: 'active', projectPath, isAgent, visualState: 'idle' });
      const activeTerminalIds = new Map(state.activeTerminalIds);
      activeTerminalIds.set(projectPath, id);
      // Reap closed sessions if total exceeds cap (keep memory bounded).
      // Only remove closed sessions that aren't the active terminal for any project.
      const MAX_SESSIONS = 50;
      if (sessions.size > MAX_SESSIONS) {
        const activeIds = new Set(activeTerminalIds.values());
        for (const [sid, s] of sessions) {
          if (sessions.size <= MAX_SESSIONS) break;
          if (s.status === 'closed' && !activeIds.has(sid)) {
            sessions.delete(sid);
          }
        }
      }
      return { sessions, activeTerminalIds };
    }),

  removeSession: (id) =>
    set((state) => {
      const sessions = new Map(state.sessions);
      const removed = sessions.get(id);
      sessions.delete(id);
      const activeTerminalIds = new Map(state.activeTerminalIds);
      if (removed) {
        const currentActive = activeTerminalIds.get(removed.projectPath);
        if (currentActive === id) {
          // Pick the last remaining terminal for this project
          const remaining = Array.from(sessions.values())
            .filter((s) => s.projectPath === removed.projectPath);
          activeTerminalIds.set(
            removed.projectPath,
            remaining.length > 0 ? remaining[remaining.length - 1].id : null,
          );
        }
      }
      return { sessions, activeTerminalIds };
    }),

  replaceSessionId: (oldId, newId) =>
    set((state) => {
      const oldSession = state.sessions.get(oldId);
      if (!oldSession) {
        // Placeholder was removed before the real ID arrived. Add the real
        // session anyway using the title we'd have used. Without this, the
        // PTY session exists on the daemon but has no UI tab — orphaned.
        // We can't recover the title/project here so just log; the next
        // listSessions sync will pick it up.
        return state;
      }
      const sessions = new Map(state.sessions);
      sessions.delete(oldId);
      sessions.set(newId, { ...oldSession, id: newId });
      const activeTerminalIds = new Map(state.activeTerminalIds);
      if (activeTerminalIds.get(oldSession.projectPath) === oldId) {
        activeTerminalIds.set(oldSession.projectPath, newId);
      }
      return { sessions, activeTerminalIds };
    }),

  setActiveTerminal: (projectPath, id) =>
    set((state) => {
      const activeTerminalIds = new Map(state.activeTerminalIds);
      activeTerminalIds.set(projectPath, id);
      return { activeTerminalIds };
    }),

  renameSession: (id, title) =>
    set((state) => {
      const sessions = new Map(state.sessions);
      const existing = sessions.get(id);
      if (existing) {
        sessions.set(id, { ...existing, title });
      }
      return { sessions };
    }),

  updateSessionStatus: (id, status) =>
    set((state) => {
      const sessions = new Map(state.sessions);
      const existing = sessions.get(id);
      if (existing) {
        const visualState = status === 'closed' ? 'exited' as const : existing.visualState;
        sessions.set(id, { ...existing, status, visualState });
      }
      return { sessions };
    }),

  getProjectSessions: (projectPath) => {
    return Array.from(get().sessions.values()).filter(
      (s) => s.projectPath === projectPath,
    );
  },

  getActiveTerminalId: (projectPath) => {
    return get().activeTerminalIds.get(projectPath) ?? null;
  },

  requestFocus: () => set((state) => ({ focusTrigger: state.focusTrigger + 1 })),

  setPendingCommand: (cmd) => set({ pendingCommand: cmd }),

  requestTabSwitch: () => set((state) => ({ tabSwitchRequest: state.tabSwitchRequest + 1 })),

  setRunSession: (projectPath, sessionId) =>
    set((state) => {
      const runSessionIds = new Map(state.runSessionIds);
      if (sessionId === null) {
        runSessionIds.delete(projectPath);
      } else {
        runSessionIds.set(projectPath, sessionId);
      }
      return { runSessionIds };
    }),

  setAttachedProject: (projectPath) => set({ attachedProject: projectPath }),

  updateVisualState: (id, visualState) =>
    set((state) => {
      const sessions = new Map(state.sessions);
      const existing = sessions.get(id);
      if (existing) {
        sessions.set(id, { ...existing, visualState });
      }
      return { sessions };
    }),

  markProjectInitialized: (projectPath) =>
    set((state) => {
      const initializedProjects = new Set(state.initializedProjects);
      initializedProjects.add(projectPath);
      return { initializedProjects };
    }),

  unmarkProjectInitialized: (projectPath) =>
    set((state) => {
      const initializedProjects = new Set(state.initializedProjects);
      initializedProjects.delete(projectPath);
      return { initializedProjects };
    }),

  isProjectInitialized: (projectPath) => {
    return get().initializedProjects.has(projectPath);
  },

  saveSessionsToStorage: () => {
    const state = get();
    const snapshot: PersistedTerminalState = {
      sessions: Array.from(state.sessions.entries()),
      activeTerminalIds: Array.from(state.activeTerminalIds.entries()),
      runSessionIds: Array.from(state.runSessionIds.entries()),
    };
    try {
      localStorage.setItem(PERSIST_KEY, JSON.stringify(snapshot));
    } catch {
      // localStorage may be full or unavailable
    }
  },

  loadSessionsFromStorage: () => {
    try {
      const raw = localStorage.getItem(PERSIST_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Validate shape before casting — corrupted localStorage should
      // return null rather than crash downstream .entries()/.map() calls.
      if (
        !parsed ||
        !Array.isArray(parsed.sessions) ||
        !Array.isArray(parsed.activeTerminalIds) ||
        !Array.isArray(parsed.runSessionIds)
      ) {
        return null;
      }
      return parsed as PersistedTerminalState;
    } catch {
      return null;
    }
  },

  clearPersistedStorage: () => {
    try {
      localStorage.removeItem(PERSIST_KEY);
    } catch {
      // ignore
    }
  },

  restoreFromPersistence: () => {
    const persisted = get().loadSessionsFromStorage();
    if (!persisted) return false;

    set((state) => ({
      sessions: new Map([...state.sessions, ...persisted.sessions]),
      activeTerminalIds: new Map([...state.activeTerminalIds, ...persisted.activeTerminalIds]),
      runSessionIds: new Map([...state.runSessionIds, ...persisted.runSessionIds]),
      // DON'T restore initializedProjects - we want to re-sync with daemon
    }));

    // Clear storage after restoring to avoid stale data on next restart
    get().clearPersistedStorage();
    return true;
  },

  clearInitializedProjects: () => {
    set({ initializedProjects: new Set() });
  },
}));
